import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';

// A single Faculty Card component with a new design
const FacultyCard = ({ name, title, image, quote, expertise }) => {
  const cardVariants = {
    initial: {
      background: 'linear-gradient(135deg, #2D3748, #1A202C)',
      boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
    },
    hover: {
      y: -8,
      scale: 1.03,
      boxShadow: '0 15px 30px rgba(0, 172, 193, 0.3)',
      transition: { type: 'spring', stiffness: 300, damping: 20 },
    },
  };

  const overlayVariants = {
    hidden: { opacity: 0, y: '100%' },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeInOut' } },
  };

  return (
    <motion.div
      className="faculty-card-reimagined"
      variants={cardVariants}
      initial="initial"
      whileHover="hover"
    >
      <div className="faculty-image-wrapper">
        <img src={image} alt={name} className="faculty-image-new" />
        <motion.div
          className="faculty-hover-details"
          variants={overlayVariants}
          initial="hidden"
          // Framer Motion automatically animates to the "visible" variant from the parent's `whileHover`
          animate="visible"
        >
          <p className="faculty-quote">"{quote}"</p>
          {/* <p className="faculty-expertise"><span>Expertise:</span> {expertise}</p> */}
        </motion.div>
      </div>
      <div className="faculty-content">
        <h3 className="faculty-name-new">{name}</h3>
        <p className="faculty-title-new">{title}</p>
      </div>
    </motion.div>
  );
};

// Main component to fetch data and render the grid
const FacultyCardGrid = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px 0px' });

  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFacultyData = async () => {
      try {
        const response = await axios.get("https://ethiccraft.org/backend/faculty/read_isShown.php");
        const data = response.data;
        if (data && data.length > 0) {
          const filtered = data.filter(faculty => faculty.college === 'main');
          const mapped = filtered.map(faculty => ({
            name: faculty.faculty_name,
            title: faculty.faculty_designation,
            image: `https://ethiccraft.org/${faculty.faculty_image}`,
            // Provide more meaningful fallbacks
            quote: faculty.faculty_description,
            expertise: faculty.faculty_expertise,
          }));
          setFaculties(mapped);
        } else {
          setError("No faculty records were found.");
        }
      } catch (err) {
        console.error("Error fetching faculty data:", err);
        setError("Failed to load faculty data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchFacultyData();
  }, []);

  const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15, // Stagger animation for each card
      },
    },
  };

  const cardEntryVariant = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };
  
  // Subtle, slower background shapes
  const blurredShapes = Array.from({ length: 4 }, (_, i) => (
    <motion.div
      key={i}
      className="bg-shape"
      initial={{ opacity: 0, scale: 0.5, x: `${Math.random() * 100}vw`, y: `${Math.random() * 100}vh` }}
      animate={{ opacity: 0.1, scale: 1.2 }}
      transition={{ duration: 20, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
    />
  ));

  return (
    <section className="faculty-grid-section" ref={containerRef}>
      {blurredShapes}
      <div className="faculty-section-header">
        <motion.h2
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          Meet Our Esteemed Faculty
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
        >
          Pioneers in education, guiding the leaders of tomorrow.
        </motion.p>
      </div>

      {loading && <div className="loading-message">.</div>}
      {error && <div className="error-message">{error}</div>}
      
      {!loading && !error && (
        <motion.div
          className="faculty-grid-reimagined"
          variants={gridVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {faculties.map((faculty, index) => (
            <motion.div key={index} variants={cardEntryVariant}>
              <FacultyCard {...faculty} />
            </motion.div>
          ))}
        </motion.div>
      )}
    </section>
  );
};

export default FacultyCardGrid;